// LAT-934: pure types + helpers shared between the Guardian panel (`quality-guard-entity-state.tsx`)
// and the aspect-first finding card (`finding-card.tsx`). Extracted so the card can be its own
// file without a circular import back into the panel. No React and no Convex client imports —
// `Doc` and `Id` are type-only, and `humanizeKey` is a pure string helper.

import type { Doc, Id } from "@repo/convex/dataModel";
import {
  deriveQualityGuardReadinessLabel,
  humanizeKey,
  isCountedQualityGuardIssue,
  isCoveredQualityGuardFinding,
} from "@repo/shared";

export type QualityGuardCriticality = "critical" | "high" | "recommended" | "optional" | "info";
export type QualityGuardReadinessLabel =
  | "checking"
  | "unready"
  | "needs_attention"
  | "unsafe"
  | "accepted_risk"
  | "ready";
export type QualityGuardAxis = "completeness" | "quality";
export type QualityGuardRemediationRoute =
  | "none"
  | "claudia_change_candidate"
  | "claudia_clarification"
  | "accept_risk";

export type QualityGuardEvaluationState = "complete" | "partial" | "unchecked" | "not_applicable";
export interface QualityGuardEvaluationCounts {
  judged: number;
  notApplicable: number;
  unverified: number;
  errored: number;
}

export type QualityGuardProjection = Doc<"qualityGuardReadinessProjections"> & {
  readinessLabel: QualityGuardReadinessLabel;
  // LAT-858 P5: optional, frontend-declared integration seam — the backend contract that adds
  // these to `qualityGuardReadinessProjections` rows (entity and section scope alike) lands in a
  // parallel stream. Absent means legacy "complete" behavior (no visual change).
  evaluationState?: QualityGuardEvaluationState;
  evaluationCountsJson?: QualityGuardEvaluationCounts;
};
/**
 * What the summary header reads off a projection. A full `QualityGuardProjection` satisfies
 * it, and so does a reading derived from the findings in hand, which carries no rule counts.
 */
export type QualityGuardHeaderProjection = {
  readinessLabel: QualityGuardReadinessLabel;
  checking: boolean;
  openFindingCount: number;
  openSevereFindingCount: number;
  acceptedRiskCount: number;
  completenessIssueCount?: number;
  completenessSeverity?: "none" | "yellow" | "red";
  completenessHighestCriticality?: string;
  qualityIssueCount?: number;
  qualitySeverity?: "none" | "yellow" | "red";
  qualityHighestCriticality?: string;
  criticalIssueCount?: number;
  highIssueCount?: number;
  recommendedIssueCount?: number;
  completedRuleCount?: number;
  evaluationState?: QualityGuardEvaluationState;
  evaluationCountsJson?: QualityGuardEvaluationCounts;
  qualityApplicable?: boolean;
};

export type QualityGuardSectionProjection = {
  fieldId: string;
  tabKey?: string | null;
  projection: QualityGuardHeaderProjection;
};
/** One open issue of the incumbent. */
export type QualityGuardFinding = Doc<"qualityGuardFindings"> & {
  axis: QualityGuardAxis;
  criticality: QualityGuardCriticality;
};

/** What identifies one row of the finding list: the finding's id. */
export function qualityGuardFindingRowKey(finding: QualityGuardFinding): string {
  return String(finding._id);
}
/**
 * Why this issue's Accept-risk control is closed, or `null` when it is open. The sentence
 * describes the server's refusal, so the control states the reason rather than letting the
 * click fail.
 */
export function acceptRiskRefusalReason(finding: QualityGuardFinding): string | null {
  if (finding.readinessImpact === "unsafe") {
    return "Unsafe findings must be fixed before they can clear readiness.";
  }
  return null;
}

export type QualityGuardFindingRemediation = {
  reason?: string;
  criteriaNotMet: string[];
  requiredFix?: string;
  remediationRoute?: QualityGuardRemediationRoute;
  clarificationQuestion?: string;
};
type QualityGuardAcceptedRiskFields = {
  canonicalKey: string;
  familyKey?: string | null;
  aspectId?: string | null;
  criticality: QualityGuardCriticality;
  reason?: string | null;
  acceptedBy?: Id<"users"> | string | null;
  acceptedAt: number;
  expiresAt: number | null;
  findingTitle: string;
  fieldId: string | null;
  tabKey: string | null;
  /** The impact of the finding this covers, absent on a row whose finding could not be read. */
  readinessImpact?: Doc<"qualityGuardFindings">["readinessImpact"] | null;
  /** The axis it was recorded on, which with the scope addresses the finding it covers. */
  axis?: QualityGuardAxis | null;
};
/** A standing acceptance. */
export type QualityGuardAcceptedRisk = QualityGuardAcceptedRiskFields & {
  acceptedRiskId: Id<"qualityGuardAcceptedRisks">;
  findingId: Id<"qualityGuardFindings">;
};
/**
 * LAT-934 founder decision (2026-08-24, reversing the 2026-08-23 aspect-selection points 3-5):
 * there is no aspect-level selection, so risk acceptance always targets the whole finding.
 */
export type QualityGuardRiskTarget = {
  finding: QualityGuardFinding;
};

export const severityRank: Record<QualityGuardCriticality, number> = {
  critical: 0,
  high: 1,
  recommended: 2,
  optional: 3,
  info: 4,
};

const qualityGuardRemediationRoutes = new Set<QualityGuardRemediationRoute>([
  "none",
  "claudia_change_candidate",
  "claudia_clarification",
  "accept_risk",
]);

function getRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function getOptionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
}

function getRemediationRoute(value: unknown): QualityGuardRemediationRoute | undefined {
  return qualityGuardRemediationRoutes.has(value as QualityGuardRemediationRoute)
    ? (value as QualityGuardRemediationRoute)
    : undefined;
}

function getStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0)
    : [];
}

export function getFindingRemediation(
  finding: QualityGuardFinding
): QualityGuardFindingRemediation | null {
  const payload = getRecord(finding.remediationCandidatesJson);
  const primary = getRecord(payload.primary);
  const claudia = getRecord(primary.claudia);
  const criteriaNotMet = getStringArray(primary.criteriaNotMet);
  const remediation: QualityGuardFindingRemediation = {
    reason: getOptionalString(primary.reason),
    criteriaNotMet,
    requiredFix: getOptionalString(primary.requiredFix),
    remediationRoute: getRemediationRoute(claudia.remediationRoute),
    clarificationQuestion: getOptionalString(claudia.clarificationQuestion),
  };

  if (
    !remediation.reason &&
    remediation.criteriaNotMet.length === 0 &&
    !remediation.requiredFix &&
    !remediation.remediationRoute &&
    !remediation.clarificationQuestion
  ) {
    return null;
  }

  return remediation;
}

export type QualityGuardFindingSectionLabels = {
  tabLabel?: string | null;
  fieldLabel?: string | null;
};

/**
 * The finding's "section" for display — its resolved field label when known, else the
 * humanized fieldId, else the resolved tab label, else a generic fallback. Shared by the card
 * title (completeness pattern) and the breadcrumb below.
 */
export function resolveFindingSectionLabel(
  finding: QualityGuardFinding,
  labels: QualityGuardFindingSectionLabels
): string {
  const fieldLabel = labels.fieldLabel?.trim();
  if (fieldLabel) {
    return fieldLabel;
  }
  if (finding.fieldId?.trim()) {
    return humanizeKey(finding.fieldId);
  }
  const tabLabel = labels.tabLabel?.trim();
  if (tabLabel) {
    return tabLabel;
  }
  return "This entity";
}

/**
 * LAT-934 founder decisions (point 2, 2026-08-23 preview review): the card title is a
 * section-state SENTENCE, not the raw finding title.
 *
 * - Completeness: fixed template `"<section> is incomplete"` — always, regardless of what the
 *   finding's own `title` says.
 * - Quality: the finding's own authored `title` when non-empty — rule authoring already phrases
 *   quality-axis titles as section-state sentences (the founder's own example, "Business Context
 *   contradicts itself", is a real finding title, not synthesized copy). Falls back to
 *   `"<section> has quality issues"` only when there is no title to use.
 *
 * The fallback is a defensive path, not a commonly-hit one: `qualityGuardFindings.title` is a
 * required, non-optional schema field, so a real quality finding should always have SOME title.
 * Implemented anyway per the founder's explicit "if no natural derivation exists" clause.
 */
export function resolveFindingCardTitle(
  finding: QualityGuardFinding,
  labels: QualityGuardFindingSectionLabels
): string {
  const sectionLabel = resolveFindingSectionLabel(finding, labels);
  if (finding.axis === "completeness") {
    return `${sectionLabel} is incomplete`;
  }
  const authoredTitle = finding.title?.trim();
  if (authoredTitle) {
    return authoredTitle;
  }
  return `${sectionLabel} has quality issues`;
}

/**
 * LAT-934 founder decisions (point 7): the breadcrumb/meta line must show friendly display
 * labels ("Business Scope › Initiative Overview"), never raw framework keys
 * ("Dataset.Structure › KeyJoinsAndIdentifiers"). Prefers the resolved tab/field labels (from
 * the compiled framework structure, via `resolveEntityAuthoringTabDescriptors` in the panel);
 * humanizes the raw key only as a last resort, while the real structure is still loading.
 */
export function resolveFindingCardLocation(
  finding: QualityGuardFinding,
  labels: QualityGuardFindingSectionLabels
): string {
  const tabLabel = labels.tabLabel?.trim() || (finding.tabKey ? humanizeKey(finding.tabKey) : null);
  const fieldLabel =
    labels.fieldLabel?.trim() || (finding.fieldId ? humanizeKey(finding.fieldId) : null);
  const parts = [tabLabel, fieldLabel].filter((part): part is string => Boolean(part));

  return parts.length > 0 ? parts.join(" › ") : "Entity";
}

export function sortFindings(findings: readonly QualityGuardFinding[]): QualityGuardFinding[] {
  return [...findings].sort((left, right) => {
    const severityDelta = severityRank[left.criticality] - severityRank[right.criticality];
    if (severityDelta !== 0) {
      return severityDelta;
    }
    return right.updatedAt - left.updatedAt;
  });
}

/**
 * A header reading taken from the issues themselves, for an element whose roll-up row is not
 * there yet.
 *
 * The roll-up is a row of its own, written by the aggregate that folds the issues; the issues
 * are written when a judgment settles. So an element can carry current issues while the row
 * that counts them is still pending, and a header that reads only the row then says nothing
 * has been produced while the list beneath it shows four issues. Everything here is a fact
 * about the issues in hand — their axes, their weights, the word the platform's own ladder
 * reads them by — and nothing is invented: the per-axis `severity` fields stay absent, so the
 * badges read the criticality they always fall back to.
 *
 * `null` when there is nothing to read, which is the state the header already has copy for.
 */
export function deriveHeaderProjectionFromFindings(
  findings: readonly QualityGuardFinding[],
  context: {
    checking: boolean;
    /** The standing acceptances in scope, grouped here into the findings they cover. */
    acceptedRisks?: readonly QualityGuardAcceptedRisk[];
  }
): QualityGuardHeaderProjection | null {
  if (findings.length === 0) {
    return null;
  }
  // The same exclusion the roll-up makes, so the numbers do not change when the row lands:
  // an `info` impact and the two lightest criticalities are listed and never counted.
  const issues = findings.filter(isCountedQualityGuardIssue);
  const onAxis = (axis: QualityGuardAxis) => issues.filter((finding) => finding.axis === axis);
  const heaviest = (rows: readonly QualityGuardFinding[]): QualityGuardCriticality | undefined =>
    (["critical", "high", "recommended", "optional", "info"] as const).find((criticality) =>
      rows.some((finding) => finding.criticality === criticality)
    );
  const countOf = (criticality: QualityGuardCriticality) =>
    issues.filter((finding) => finding.criticality === criticality).length;
  // The ladder is the platform's, and the rows it is asked about are not the same rows:
  // `unsafe` reads every issue in hand, counted or not, because an issue can be too light
  // to reach a badge and still stop the scope being relied on. Everything below it reads
  // the counted set.
  // Coverage is a fact about a finding, not about the acceptance list being non-empty: a
  // finding with one key accepted and another still open is one the reader still owes
  // something on, however light the open key is. An issue row exists for every open key, so
  // an acceptance is covered exactly when no open row answers to the same issue.
  //
  // Matched by scope and axis, never by the finding id the acceptance recorded: a re-scan
  // supersedes the finding and mints a new one, so that id names a row nobody lists any
  // more while the keys it was accepted against are open on the replacement.
  //
  // Counted per FINDING, which is what the roll-up counts: a finding whose three keys were
  // each accepted is three acceptance rows and one covered finding, and reporting three
  // would make the Accepted badge drop to one the moment the row lands.
  //
  // Scope and axis address the finding, and `familyKey` tells two of them apart where one
  // scope carries more than one: an acceptance carries the family of the finding it covers, so
  // several rule families failing on one field and axis count once each.
  const coveredFindings = new Set<string>();
  for (const risk of context.acceptedRisks ?? []) {
    const covered = isCoveredQualityGuardFinding({
      hasOpenKey: findings.some((finding) => riskSharesFindingScope(risk, finding)),
      hasAcceptedKey: true,
      readinessImpact: risk.readinessImpact,
    });
    if (covered) {
      coveredFindings.add(
        `${risk.axis ?? ""}::${risk.tabKey ?? ""}::${risk.fieldId ?? ""}::${risk.familyKey ?? ""}`
      );
    }
  }
  const acceptedRiskCount = coveredFindings.size;
  const readinessLabel = deriveQualityGuardReadinessLabel({
    current: findings,
    counted: issues,
    hasAcceptedRisk: acceptedRiskCount > 0,
  });

  const completeness = onAxis("completeness");
  const quality = onAxis("quality");
  const criticalIssueCount = countOf("critical");
  const highIssueCount = countOf("high");
  return {
    readinessLabel: context.checking ? "checking" : readinessLabel,
    checking: context.checking,
    openFindingCount: issues.length,
    openSevereFindingCount: criticalIssueCount + highIssueCount,
    acceptedRiskCount,
    completenessIssueCount: completeness.length,
    ...(heaviest(completeness) ? { completenessHighestCriticality: heaviest(completeness) } : {}),
    qualityIssueCount: quality.length,
    ...(heaviest(quality) ? { qualityHighestCriticality: heaviest(quality) } : {}),
    criticalIssueCount,
    highIssueCount,
    recommendedIssueCount: countOf("recommended"),
  };
}

export function findingMatchesSection(
  finding: QualityGuardFinding,
  section: { tabKey?: string | null; fieldId?: string | null }
): boolean {
  if (!section.fieldId) {
    return true;
  }
  if (finding.fieldId !== section.fieldId) {
    return false;
  }
  return !section.tabKey || !finding.tabKey || finding.tabKey === section.tabKey;
}

export function riskMatchesSection(
  risk: QualityGuardAcceptedRisk,
  section: { tabKey?: string | null; fieldId?: string | null }
): boolean {
  if (!section.fieldId) {
    return true;
  }
  if (risk.fieldId !== section.fieldId) {
    return false;
  }
  return (risk.tabKey ?? null) === (section.tabKey ?? null);
}

/**
 * Whether one open issue row belongs to the finding an acceptance was recorded against.
 *
 * A scope carries one finding per axis, so the scope and the axis address the finding —
 * never the finding id the acceptance stored, which a re-scan supersedes while the keys it
 * covers live on in the replacement. Coverage is a fact about the finding and not about the
 * one key: a sibling key still open leaves the whole finding owing something.
 *
 * Each half is compared only where both sides name it, the same defensiveness
 * {@link riskMatchesFinding} applies to the tab: a row that names no axis matches on scope.
 */
function riskSharesFindingScope(
  risk: QualityGuardAcceptedRisk,
  finding: QualityGuardFinding
): boolean {
  if (risk.fieldId !== (finding.fieldId ?? null)) {
    return false;
  }
  if (risk.tabKey && finding.tabKey && risk.tabKey !== finding.tabKey) {
    return false;
  }
  return !risk.axis || risk.axis === finding.axis;
}

export function riskMatchesFinding(
  risk: QualityGuardAcceptedRisk,
  finding: QualityGuardFinding
): boolean {
  if ((risk.familyKey ?? risk.canonicalKey) !== finding.canonicalKey) {
    return false;
  }
  if (risk.fieldId !== (finding.fieldId ?? null)) {
    return false;
  }
  if (risk.tabKey && finding.tabKey && risk.tabKey !== finding.tabKey) {
    return false;
  }
  return true;
}

// --- LAT-934: aspect-first grouping --------------------------------------------------------
//
// Three groups ship now (Completeness / Quality / Accepted — the founder's 2026-08-23 preview
// review kept these as the design's original axis names rather than the "Incomplete"/"Poor
// quality" rewrite an earlier round tried). Product has named a fourth for later ("Needs
// confirmation" — see specs/LAT-934-guardian-panel-redesign/PLAN.md) that waits on a third axis
// and is deliberately NOT added yet. Adding it later is: append a `{ key, label, matches }` entry
// to `QUALITY_GUARD_OPEN_FINDING_GROUPS` below (or a sibling list, for a group keyed off
// something other than a finding predicate) — the render loop in `quality-guard-entity-state.tsx`
// iterates this array and needs no changes.

export type QualityGuardOpenFindingGroupKey = "completeness" | "quality";

export interface QualityGuardOpenFindingGroupDef {
  key: QualityGuardOpenFindingGroupKey;
  label: string;
  matches: (finding: QualityGuardFinding) => boolean;
}

export const QUALITY_GUARD_OPEN_FINDING_GROUPS: readonly QualityGuardOpenFindingGroupDef[] = [
  {
    key: "completeness",
    label: "Completeness",
    matches: (finding) => finding.axis === "completeness",
  },
  { key: "quality", label: "Quality", matches: (finding) => finding.axis === "quality" },
];

export interface QualityGuardOpenFindingGroup extends QualityGuardOpenFindingGroupDef {
  items: QualityGuardFinding[];
}

/** Buckets already-sorted open findings into the ordered group defs, dropping empty groups. */
export function groupOpenFindings(
  findings: readonly QualityGuardFinding[]
): QualityGuardOpenFindingGroup[] {
  return QUALITY_GUARD_OPEN_FINDING_GROUPS.map((group) => ({
    ...group,
    items: findings.filter(group.matches),
  })).filter((group) => group.items.length > 0);
}

/** The "Accepted" group's label — kept alongside the open-group labels for one consistent header style. */
export const QUALITY_GUARD_ACCEPTED_GROUP_LABEL = "Accepted";
